import {
  Euler,
  type Object3D,
  Quaternion,
  Vector2,
  Vector3,
  type WebXRManager,
} from "three";
import { isMobileDevice } from "@/utils/device-detection";
import {
  ControlConstants as CC,
  type MovementScheme,
} from "./control-constants";
import type { ControlMode } from "./control-types";
import { TorchcamCameraData, torchcamToCamera } from "@/transforms";

export class MarbleControls {
  fpsControls: FpsControls;
  interControls: InterControls;
  lastTime = 0;
  controlMode: ControlMode;

  constructor(initialMode: ControlMode = "fps") {
    this.controlMode = initialMode;
    this.fpsControls = new FpsControls({ mode: initialMode });
    this.interControls = new InterControls();
    this.interControls.setScrollVerticalEnabled(initialMode === "flight");
  }

  setCamera(camera: Object3D) {
    this.interControls.setCamera(camera);
  }

  setInitialFov(fov: number) {
    this.interControls.setInitialFov(fov);
  }

  setFovTargetChangeCallback(callback: (targetFov: number) => void) {
    this.interControls.setFovTargetChangeCallback(callback);
  }

  initialize(canvas: HTMLCanvasElement) {
    this.interControls.initialize(canvas);
  }

  dispose() {
    this.interControls.dispose();
  }

  update(control: Object3D) {
    const time = performance.now();
    const deltaTime = (time - (this.lastTime || time)) / 1000;
    this.lastTime = time;

    this.fpsControls.setMode(this.controlMode);
    this.fpsControls.update(deltaTime, control);
    this.interControls.update(deltaTime, control);

    // Clamp position to a fixed radius from origin
    const radius = control.position.length();
    if (radius > MOVEMENT_RADIUS_LIMIT) {
      // console.log("[MarbleControls] Clamping position to radius", MOVEMENT_RADIUS_LIMIT);
      control.position.multiplyScalar(MOVEMENT_RADIUS_LIMIT / radius);
    }
  }

  triggerBurst(direction: "forward" | "backward" = "forward") {
    this.interControls.triggerBurstMovement(direction);
  }

  triggerReturn() {
    this.fpsControls.stopMovement();
    this.interControls.triggerReturnToOrigin();
  }

  setJoystickVerticalEnabled(enabled: boolean) {
    this.fpsControls.setJoystickVerticalEnabled(enabled);
  }

  setJoystickPosition(center: Vector2 | null) {
    this.interControls.setJoystickPosition(center);
  }

  setControlMode(mode: ControlMode) {
    if (this.controlMode === mode) {
      this.interControls.setScrollVerticalEnabled(mode === "flight");
      return;
    }
    this.controlMode = mode;
    this.fpsControls.setMode(mode);
    this.interControls.setScrollVerticalEnabled(mode === "flight");
  }

  getControlMode(): ControlMode {
    return this.controlMode;
  }

  setLookJoystickInput(x: number, y: number) {
    this.interControls.setLookInput(x, y * CC.VERTICAL_LOOK_SENSITIVITY);
  }

  //TODO: please sanity check that this is correct with the new controller setup
  setFromServer(cameraData: TorchcamCameraData) {
    if (!this.interControls?.camera) {
      console.warn("[MarbleControls] Cannot apply server camera: missing camera");
      return;
    }

    if(import.meta.env.MODE === "development") {
      console.log("applying server camera");
    }

    torchcamToCamera(cameraData, this.interControls.camera as any);
    // this.interControls.camera.updateMatrixWorld?.();
    // this.interControls.camera.updateProjectionMatrix?.();
    this.fpsControls.stopMovement();
    this.interControls.rotateVelocity.set(0, 0, 0);
    this.interControls.moveVelocity.set(0, 0, 0);
    this.interControls.scroll.set(0, 0, 0);
    this.interControls.burst.active = false;
    this.interControls.originState.active = false;
    if (
      this.interControls.camera &&
      typeof (this.interControls.camera as { fov?: number }).fov === "number"
    ) {
      // @ts-expect-error - camera is a PerspectiveCamera
      const fov = (this.interControls.camera as { fov: number }).fov;
      this.interControls.setInitialFov(fov);
    } else {
      console.error("[MarbleControls] Cannot apply server camera: camera is not a PerspectiveCamera");
    }
  }
}

const USE_EASING_AND_INERTIA = false;
const MOVEMENT_RADIUS_LIMIT = 2.0;

//TOD: implement physics
const USE_PHYSICS = import.meta.env.VITE_USE_COLLISION === "true";

export class FpsControls {
  moveSpeed: number;
  speedMultiplier: number;

  // joystick-specific properties
  joystickVerticalEnabled = false;
  controlMode: ControlMode;

  // joystick input (for mobile)
  joystickInput: Vector2;
  isJoystickActive: boolean;

  private readonly joystickInputSmooth: Vector2;
  private readonly joystickEasing: number;
  private hasJoystickMomentum = false;

  // inertia properties
  moveInertia: number;
  currentVelocity: Vector3;
  targetVelocity: Vector3;
  private verticalVelocity = 0;
  private groundHeight: number | null = null;
  private grounded = false;
  private gravity: number;
  private readonly jumpImpulse: number;

  movementScheme: MovementScheme;
  keycodeMoveMapping: { [key: string]: Vector3 };
  gamepadMapping: {
    [button: number]: "shift" | "ctrl";
  };
  xr?: WebXRManager;
  useRFControls = true;

  // movement state
  enable = true;
  keydown: { [key: string]: boolean };
  keycode: { [key: string]: boolean };

  // small-value clamps
  private readonly SMALL_VELOCITY_EPS = 0.1; // magnitude threshold

  constructor({
    movementScheme = CC.MovementScheme.WASD_SPACE_CTRL,
    keycodeMoveMapping,
    gamepadMapping,
    xr,
    mode = "fps",
  }: {
    movementScheme?: MovementScheme;
    keycodeMoveMapping?: { [key: string]: Vector3 };
    gamepadMapping?: {
      [button: number]: "shift" | "ctrl";
    };
    xr?: WebXRManager;
    mode?: ControlMode;
  } = {}) {
    this.moveSpeed = CC.MOVEMENT_SPEED;
    this.speedMultiplier = CC.SPEED_MULTIPLIER;

    this.joystickInput = new Vector2();
    this.isJoystickActive = false;
    this.joystickInputSmooth = new Vector2();
    this.joystickEasing = CC.JOYSTICK_EASING;
    this.controlMode = mode;

    this.moveInertia = CC.MOVE_INERTIA;
    this.currentVelocity = new Vector3();
    this.targetVelocity = new Vector3();
    this.gravity = CC.GRAVITY_ACCELERATION;
    this.jumpImpulse = CC.JUMP_IMPULSE;

    this.movementScheme = movementScheme;
    const verticalKeys =
      movementScheme === CC.MovementScheme.WASD_RF
        ? CC.RF_KEYCODE_MOVE
        : CC.SPACE_CTRL_KEYCODE_MOVE;
    this.keycodeMoveMapping = keycodeMoveMapping ?? {
      ...CC.WASD_KEYCODE_MOVE,
      ...verticalKeys,
    };
    this.gamepadMapping = gamepadMapping ?? {
      6: "ctrl",
      7: "shift",
    };
    this.xr = xr;

    this.keydown = {};
    this.keycode = {};

    this.setupEventListeners();
    this.updateKeyMapping();
    this.applyMode(mode);
  }

  private setupEventListeners() {
    document.addEventListener("keydown", (e) => {
      this.keydown[e.key] = true;
      this.keycode[e.code] = true;
    });

    document.addEventListener("keyup", (e) => {
      this.keydown[e.key] = false;
      this.keycode[e.code] = false;
    });

    window.addEventListener("blur", () => {
      this.keydown = {};
      this.keycode = {};
    });

    document.addEventListener("contextmenu", () => {
      this.keydown = {};
      this.keycode = {};
    });

    // clear keys when focus leaves the document
    document.addEventListener("focusout", (e) => {
      // only clear if focus is moving outside the document or to a different window
      if (!(e.relatedTarget && document.contains(e.relatedTarget as Node))) {
        this.keydown = {};
        this.keycode = {};
      }
    });

    // clear keys on vis change (covers tab switching, minimizing, etc.)
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.keydown = {};
        this.keycode = {};
      }
    });

    // clear keys when mouse leaves browser
    document.addEventListener("mouseleave", () => {
      this.keydown = {};
      this.keycode = {};
    });

    window.addEventListener("focus", () => {
      this.keydown = {};
      this.keycode = {};
    });

    document.addEventListener("keydown", (e) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        (e.key === "4" || e.code === "Digit4")
      ) {
        // clear all keys immediately
        // let system
        setTimeout(() => {
          this.keydown = {};
          this.keycode = {};
        }, 100);
      }
    });
  }

  updateKeyMapping() {
    const verticalKeys = this.useRFControls
      ? CC.RF_KEYCODE_MOVE
      : CC.SPACE_CTRL_KEYCODE_MOVE;

    this.keycodeMoveMapping = {
      ...CC.WASD_KEYCODE_MOVE,
      ...verticalKeys,
    };
  }

  setUseCSControls(useCS: boolean) {
    this.useRFControls = !useCS;
    this.updateKeyMapping();
  }

  private applyMode(mode: ControlMode) {
    if (mode === "fps") {
      this.setUseCSControls(true);
      this.setJoystickVerticalEnabled(false);
      this.groundHeight = null;
      this.verticalVelocity = 0;
      this.grounded = false;
    } else {
      this.setUseCSControls(false);
      this.setJoystickVerticalEnabled(true);
      this.groundHeight = null;
      this.verticalVelocity = 0;
      this.grounded = false;
    }
  }

  setMode(mode: ControlMode) {
    if (this.controlMode === mode) {
      return;
    }

    this.controlMode = mode;
    this.applyMode(mode);
  }

  setJoystickVerticalEnabled(enabled: boolean) {
    this.joystickVerticalEnabled = enabled;
  }

  stopMovement() {
    // clear joystick input immediately
    this.joystickInput.set(0, 0);
    this.joystickInputSmooth.set(0, 0);
    this.isJoystickActive = false;
    this.hasJoystickMomentum = false;
    // clear velocities (no rubber banding)
    this.currentVelocity.set(0, 0, 0);
    this.targetVelocity.set(0, 0, 0);
  }

  updateJoystickInput(x: number, y: number) {
    const magnitude = Math.sqrt(x * x + y * y);

    if (magnitude < CC.JOYSTICK_DEADZONE_RADIUS) {
      this.joystickInput.set(0, 0);
      this.isJoystickActive = false;
      return;
    }

    // normalize and apply sensitivity
    const normalizedX =
      (x / magnitude) * Math.min(magnitude, 1.0) * CC.JOYSTICK_SENSITIVITY;
    const normalizedY =
      (y / magnitude) * Math.min(magnitude, 1.0) * CC.JOYSTICK_SENSITIVITY;

    // gentle easing to the joystick input
    const targetInput = new Vector2(normalizedX, normalizedY);
    if (USE_EASING_AND_INERTIA) {
      this.joystickInputSmooth.lerp(targetInput, this.joystickEasing);
    } else {
      this.joystickInputSmooth.copy(targetInput);
    }

    this.joystickInput.copy(this.joystickInputSmooth);
    this.isJoystickActive = true;
    this.hasJoystickMomentum = true;
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <complexity is justified to handle all cases of joystick movement/state>
  update(deltaTime: number, control: Object3D) {
    if (!this.enable) {
      return;
    }

    // calculate target velocity from input
    this.targetVelocity.set(0, 0, 0);

    // keyboard input
    for (const [keycode, move] of Object.entries(this.keycodeMoveMapping)) {
      if (this.keycode[keycode]) {
        this.targetVelocity.add(move);
        if (move.lengthSq() > 0) {
          this.hasJoystickMomentum = false;
        }
      }
    }

    if (this.isJoystickActive) {
      this.targetVelocity.add(
        new Vector3(this.joystickInput.x, 0, this.joystickInput.y)
      );
    }

    if (this.targetVelocity.lengthSq() > 1) {
      // normalize/scale target velocity
      this.targetVelocity.normalize();
    }

    if (this.controlMode === "fps") {
      this.targetVelocity.y = 0;
    }

    // apply speed and multipliers
    this.targetVelocity.multiplyScalar(this.moveSpeed);

    if (this.targetVelocity.lengthSq() > 0) {
      // apply inertia
      // ease moving towards target
      if (USE_EASING_AND_INERTIA) {
        this.currentVelocity.lerp(
          this.targetVelocity,
          1 - Math.exp((-deltaTime / this.moveInertia) * 8)
        );
      } else {
        this.currentVelocity.copy(this.targetVelocity);
      }
    } else {
      // decelerating with inertia
      if (USE_EASING_AND_INERTIA) {
        this.currentVelocity.multiplyScalar(
          Math.exp(-deltaTime / this.moveInertia)
        );
      } else {
        this.currentVelocity.set(0, 0, 0);
      }
    }

    if (this.controlMode === "fps") {
      this.currentVelocity.y = 0;
    } else {
      this.verticalVelocity = 0;
    }

    if (this.currentVelocity.lengthSq() < this.SMALL_VELOCITY_EPS * this.SMALL_VELOCITY_EPS) {
      // stop micro-movements
      this.currentVelocity.set(0, 0, 0);
      this.hasJoystickMomentum = false;
    }

    // apply movement in world space
    const yawEuler = new Euler().setFromQuaternion(control.quaternion, "YXZ");
    const yawOnlyQuaternion = new Quaternion().setFromEuler(
      new Euler(0, yawEuler.y, 0, "YXZ")
    );

    const horizontalVelocity = new Vector3(
      this.currentVelocity.x,
      0,
      this.currentVelocity.z
    );

    if (this.controlMode === "flight") {
      const appliedHorizontal = horizontalVelocity
        .clone()
        .applyQuaternion(control.quaternion);
      const verticalVelocity = new Vector3(0, this.currentVelocity.y, 0);
      const finalVelocity = appliedHorizontal.add(verticalVelocity);
      control.position.add(finalVelocity.multiplyScalar(deltaTime));
      this.grounded = false;
    } else {
      const appliedHorizontal = horizontalVelocity.applyQuaternion(
        yawOnlyQuaternion
      );
      control.position.add(appliedHorizontal.multiplyScalar(deltaTime));
      this.applyGravity(deltaTime, control);
    }
  }

  private applyGravity(deltaTime: number, control: Object3D) {
    if (this.controlMode !== "fps") {
      return;
    }

    if (this.groundHeight === null) {
      this.groundHeight = control.position.y;
      this.grounded = true;
    }

    const wantsJump = Boolean(this.keycode.Space);
    const wantsFastFall = Boolean(
      this.keycode.ControlLeft ||
      this.keycode.ControlRight ||
      this.keycode.KeyF
    );

    const groundThreshold = 0.01;
    const isOnGround =
      this.groundHeight !== null &&
      control.position.y <= this.groundHeight + groundThreshold &&
      this.verticalVelocity <= 0.01;

    if (wantsJump && isOnGround) {
      this.verticalVelocity = this.jumpImpulse;
      this.grounded = false;
    }

    const gravity = this.gravity * (wantsFastFall ? 1.4 : 1.0);
    this.verticalVelocity -= gravity * deltaTime;

    let nextY = control.position.y + this.verticalVelocity * deltaTime;

    if (this.groundHeight !== null && nextY <= this.groundHeight) {
      nextY = this.groundHeight;
      this.verticalVelocity = 0;
      this.grounded = true;
    } else {
      this.grounded = false;
    }

    control.position.y = nextY;
  }
}

type PointerState = {
  initial: Vector2;
  last: Vector2;
  position: Vector2;
  pointerId: number;
  button?: number;
  timeStamp: DOMHighResTimeStamp;
};

type BurstState = {
  active: boolean;
  startTime: number;
  duration: number; // ms
  direction: number; // 1 (forward), -1 (backward)
  lastProgress: number; // 0 to 1
};

type OriginState = {
  active: boolean;
  startTime: number;
  duration: number; // ms
  startPos: Vector3;
  startQuat: Quaternion;
};

export class InterControls {
  canvas: HTMLCanvasElement | null = null;
  camera: Object3D | null = null;
  isInitialized = false;
  onFovTargetChange?: (targetFov: number) => void;

  isTouch = false;

  rotateSpeed: number;
  scrollSpeed: number;
  panFriction: number;
  panInertia: number;

  burst: BurstState;
  originState: OriginState;

  scroll: Vector3;
  reverseScroll: boolean;
  scrollVerticalEnabled = false;

  invertX: boolean;
  invertY: boolean;

  joystickExclusionRadius = 100;
  joystickCenter: Vector2 | null = null;

  // pointer state for active rotating/panning pointer
  panning: PointerState | null;
  // pointer state for active sliding pointer (two-finger operations)
  sliding: PointerState | null;
  dualPress: boolean;

  rotateVelocity: Vector3;
  moveVelocity: Vector3;
  lookInput: Vector2;
  lookInputSmooth: Vector2;
  private readonly lookSpeed: number;
  private readonly lookEasing: number;

  // small-value clamps
  private readonly SMALL_PAN_EPS = 0.001; // normalized pan magnitude
  private readonly SMALL_LOOK_EPS = 0.001; // look input magnitude
  private readonly SMALL_SCROLL_EPS = 0.001; // scroll vector magnitude
  private readonly SMALL_ROTATE_EPS = 0.001; // rotation velocity magnitude

  private readonly DUAL_PRESS_MS = 100;
  private readonly DOUBLE_PRESS_LIMIT_MS = 300;
  private readonly DOUBLE_PRESS_DISTANCE = 50;
  private readonly isMobile: boolean;

  // double press detection
  lastUp: { position: Vector2; time: number } | null;
  doublePress: ({
    position,
    intervalMs,
  }: {
    position: Vector2;
    intervalMs: number;
  }) => void;

  constructor() {
    this.rotateSpeed = CC.ROTATION_SPEED;
    this.scrollSpeed = CC.SCROLL_SPEED;
    this.panFriction = CC.PAN_FRICTION;
    this.panInertia = CC.PAN_INERTIA;

    this.scroll = new Vector3();
    this.reverseScroll = false;

    this.burst = {
      active: false,
      startTime: 0,
      duration: CC.BURST_DURATION,
      direction: 1,
      lastProgress: 0,
    };
    this.originState = {
      active: false,
      startTime: 0,
      duration: 600,
      startPos: new Vector3(),
      startQuat: new Quaternion(),
    };

    this.isMobile = isMobileDevice();
    this.invertX = false;
    this.invertY = false;

    this.panning = null;
    this.sliding = null;
    this.dualPress = false;
    this.rotateVelocity = new Vector3();
    this.moveVelocity = new Vector3();
    this.lastUp = null;
    this.doublePress = this.defaultDoublePress;
    this.lookInput = new Vector2();
    this.lookInputSmooth = new Vector2();
    this.lookSpeed = CC.LOOK_JOYSTICK_SPEED;
    this.lookEasing = CC.LOOK_JOYSTICK_EASING;

    // initialize FOV defaults respecting mobile vs desktop initial camera fov
    this.fovStart = this.isMobile ? 105 : 75;
    this.fovTarget = this.fovStart;
  }

  // FOV interpolation state
  private fovAnimating = false;
  private fovStart = 75; // deg
  private fovTarget = 75; // deg

  private clampFovDeg(v: number): number {
    return Math.max(CC.FOV_MIN_DEG, Math.min(CC.FOV_MAX_DEG, v));
  }

  private setFovTargetDeg(target: number, current: number) {
    this.fovStart = current;
    const roundedTarget =
      Math.round(target / CC.FOV_DELTA_DEG) * CC.FOV_DELTA_DEG;
    this.fovTarget = this.clampFovDeg(roundedTarget);
    this.fovAnimating = true;
    this.onFovTargetChange?.(this.fovTarget);
  }

  setJoystickPosition(center: Vector2 | null) {
    this.joystickCenter = center;
  }

  setScrollVerticalEnabled(enabled: boolean) {
    this.scrollVerticalEnabled = enabled;
  }

  setLookInput(x: number, y: number) {
    const magnitude = Math.sqrt(x * x + y * y);
    if (magnitude > 1) {
      this.lookInput.set(x / magnitude, y / magnitude);
      return;
    }
    this.lookInput.set(x, y);
  }

  setFovTargetChangeCallback(callback: (targetFov: number) => void) {
    this.onFovTargetChange = callback;
  }

  setInitialFov(fov: number) {
    this.fovStart = fov;
    this.fovTarget = fov;
  }

  initialize(canvas: HTMLCanvasElement) {
    if (this.isInitialized) {
      this.dispose();
    }

    this.canvas = canvas;
    this.setupEventListeners();
    this.isInitialized = true;
  }

  setCamera(camera: Object3D) {
    this.camera = camera;
  }

  dispose() {
    if (!(this.isInitialized && this.canvas)) {
      return;
    }

    this.removeEventListeners();
    this.canvas = null;
    this.isInitialized = false;
  }

  private readonly defaultDoublePress = (): void => {
    // default no-op function for double press events
  };

  private isInJoystickZone(pos: Vector2): boolean {
    if (!this.joystickCenter) {
      return false;
    }
    return pos.distanceTo(this.joystickCenter) < this.joystickExclusionRadius;
  }

  private setupEventListeners() {
    if (!this.canvas) {
      return;
    }

    // Mouse events
    this.canvas.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseup", this.onMouseUp);

    // Touch events
    this.canvas.addEventListener("touchstart", this.onTouchStart, {
      passive: false,
    });
    this.canvas.addEventListener("touchmove", this.onTouchMove, {
      passive: false,
    });
    this.canvas.addEventListener("touchend", this.onTouchEnd, {
      passive: false,
    });
    this.canvas.addEventListener("touchcancel", this.onTouchEnd, {
      passive: false,
    });

    // Special movement keys
    // document.addEventListener("keydown", this.onSpecialKey);

    // Wheel/scroll events
    this.canvas.addEventListener("wheel", this.onWheel);

    // Prevent context menu
    this.canvas.addEventListener("contextmenu", this.onContextMenu);
  }

  private removeEventListeners() {
    if (!this.canvas) {
      return;
    }

    // Mouse events
    this.canvas.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);

    // Touch events
    this.canvas.removeEventListener("touchstart", this.onTouchStart);
    this.canvas.removeEventListener("touchmove", this.onTouchMove);
    this.canvas.removeEventListener("touchend", this.onTouchEnd);
    this.canvas.removeEventListener("touchcancel", this.onTouchEnd);

    // Special movement events
    // document.removeEventListener("keydown", this.onSpecialKey);

    // Wheel/scroll events
    this.canvas.removeEventListener("wheel", this.onWheel);

    // Context menu
    this.canvas.removeEventListener("contextmenu", this.onContextMenu);
  }

  private readonly onContextMenu = (e: Event) => e.preventDefault();

  private ease3(t: number): number {
    // ease-out cubic
    return 1 - (1 - t) ** 3;
  }

  private getPointerPosition(clientX: number, clientY: number): Vector2 {
    if (!this.canvas) {
      return new Vector2();
    }
    const rect = this.canvas.getBoundingClientRect();
    return new Vector2(clientX - rect.left, clientY - rect.top);
  }

  onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) {
      return;
    }

    const position = this.getPointerPosition(e.clientX, e.clientY);

    if (this.isInJoystickZone(position)) {
      return;
    }

    this.isTouch = false;

    const initial = position.clone();
    const last = position.clone();
    const { timeStamp } = e;

    // mouse always starts panning
    if (!this.panning) {
      this.panning = {
        initial,
        last,
        position,
        pointerId: -1, // -1 for mouse
        timeStamp,
      };
      this.dualPress = false;
    }
  };

  onMouseMove = (e: MouseEvent) => {
    if (!this.panning || this.panning.pointerId !== -1) {
      return;
    }

    const position = this.getPointerPosition(e.clientX, e.clientY);
    this.panning.position = position;
  };

  onMouseUp = (e: MouseEvent) => {
    if (e.button !== 0 || !this.panning || this.panning.pointerId !== -1) {
      return;
    }

    const position = this.getPointerPosition(e.clientX, e.clientY);

    // handle double click detection
    const lastUp = this.lastUp;
    this.lastUp = { position, time: e.timeStamp };

    if (lastUp) {
      const distance = lastUp.position.distanceTo(position);
      if (distance < this.DOUBLE_PRESS_DISTANCE) {
        const intervalMs = e.timeStamp - lastUp.time;
        if (intervalMs < this.DOUBLE_PRESS_LIMIT_MS) {
          this.lastUp = null;
          this.doublePress({ position, intervalMs });
        }
      }
    }

    this.panning = null;
  };

  onTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    this.isTouch = true;

    for (const touch of e.changedTouches) {
      const position = this.getPointerPosition(touch?.clientX, touch?.clientY);

      if (this.isInJoystickZone(position)) {
        continue;
      }

      const initial = position.clone();
      const last = position.clone();
      const { timeStamp } = e;

      // first touch becomes panning pointer
      if (!this.panning) {
        this.panning = {
          initial,
          last,
          position,
          pointerId: touch.identifier,
          timeStamp,
        };
        this.dualPress = false;
      } else if (!this.sliding) {
        this.sliding = {
          initial,
          last,
          position,
          pointerId: touch.identifier,
          timeStamp,
        };

        // check if we press both touches at roughly the same time
        this.dualPress =
          timeStamp - this.panning.timeStamp < this.DUAL_PRESS_MS;
      }
    }
  };

  onTouchMove = (e: TouchEvent) => {
    e.preventDefault();

    for (const touch of e.changedTouches) {
      const position = this.getPointerPosition(touch.clientX, touch.clientY);

      if (this.panning?.pointerId === touch.identifier) {
        this.panning.position = position;
      } else if (this.sliding?.pointerId === touch.identifier) {
        this.sliding.position = position;
      }
    }
  };

  private handleDoubleTapDetection(
    position: Vector2,
    timeStamp: DOMHighResTimeStamp
  ) {
    const lastUp = this.lastUp;
    this.lastUp = { position, time: timeStamp };

    if (lastUp) {
      const distance = lastUp.position.distanceTo(position);
      if (distance < this.DOUBLE_PRESS_DISTANCE) {
        const intervalMs = timeStamp - lastUp.time;
        if (intervalMs < this.DOUBLE_PRESS_LIMIT_MS) {
          this.lastUp = null;
          this.doublePress({ position, intervalMs });
        }
      }
    }
  }

  onTouchEnd = (e: TouchEvent) => {
    e.preventDefault();

    for (const touch of e.changedTouches) {
      const position = this.getPointerPosition(touch.clientX, touch.clientY);

      if (this.panning?.pointerId === touch.identifier) {
        this.handleDoubleTapDetection(position, e.timeStamp);
        this.panning = null;

        if (this.dualPress && this.sliding) {
          this.sliding = null;
        }
      } else if (this.sliding?.pointerId === touch.identifier) {
        this.sliding = null;

        if (this.dualPress && this.panning) {
          this.panning = null;
        }
      }
    }
  };

  onSpecialKey = (e: KeyboardEvent) => {
    switch (e.key) {
      case "0":
        this.triggerReturnToOrigin();
        break;
      case "[":
        {
          // decrease FOV
          const camera =
            this.camera && "fov" in this.camera
              ? (this.camera as { fov: number })
              : null;
          const current = camera?.fov ?? (this.isMobile ? 105 : 75);
          this.setFovTargetDeg(current - CC.FOV_DELTA_DEG, current);
        }
        break;
      case "]":
        {
          // increase FOV
          const camera =
            this.camera && "fov" in this.camera
              ? (this.camera as { fov: number })
              : null;
          const current = camera?.fov ?? (this.isMobile ? 105 : 75);
          this.setFovTargetDeg(current + CC.FOV_DELTA_DEG, current);
        }
        break;
      case "-":
      case "_":
        this.triggerBurstMovement("backward");
        break;
      case "+":
      case "=":
        this.triggerBurstMovement("forward");
        break;
      default:
        // no-op or add any fallback logic if needed
        break;
    }
  };

  onWheel = (e: WheelEvent) => {
    // Use wheel to adjust FoV instead of translating position
    const absX = Math.abs(e.deltaX);
    const absY = Math.abs(e.deltaY);
    const absZ = Math.abs(e.deltaZ);
    const primaryDelta = absY >= absX && absY >= absZ ? e.deltaY : absX >= absZ ? e.deltaX : e.deltaZ;

    const sign = Math.sign(primaryDelta);
    if (sign !== 0) {
      const direction = this.reverseScroll ? -sign : sign;
      const camera =
        this.camera && "fov" in this.camera
          ? (this.camera as { fov: number })
          : null;
      const current = camera?.fov ?? (this.isMobile ? 105 : 75);
      this.setFovTargetDeg(current + direction * CC.FOV_DELTA_DEG, current);
    }
    e.preventDefault();
  };

  triggerBurstMovement(direction: "forward" | "backward" = "forward") {
    this.burst.active = true;
    this.burst.startTime = performance.now();
    this.burst.duration = CC.BURST_DURATION;
    this.burst.direction = direction === "forward" ? -1 : 1;
    this.burst.lastProgress = 0;
  }

  private updateBurstMovement() {
    if (!this.burst.active) {
      return;
    }

    const now = performance.now();
    const elapsed = now - this.burst.startTime;
    const progress = Math.min(elapsed / this.burst.duration, 1);

    // easing function + amount to apply this frame
    const easedNow = this.ease3(progress);
    const easedLast = this.ease3(this.burst.lastProgress);
    const easedDelta = easedNow - easedLast;

    // Apply the burst movement for this frame
    const delta = CC.BURST_SCROLL_DELTA * easedDelta * this.burst.direction;
    this.scroll.add(new Vector3(0, delta, 0));

    this.burst.lastProgress = progress;
    if (progress >= 1) {
      this.burst.active = false;
    }
  }

  triggerReturnToOrigin() {
    this.rotateVelocity.set(0, 0, 0);
    this.moveVelocity.set(0, 0, 0);

    this.originState.active = true;
    this.originState.startTime = performance.now();
    this.originState.startPos = new Vector3();
    this.originState.startQuat = new Quaternion();
  }

  private updateReturnToOrigin(control: Object3D) {
    if (!this.originState.active) {
      return;
    }

    // capture pos/rot on first update call
    if (this.originState.startPos.length() === 0) {
      this.originState.startPos = control.position.clone();
      this.originState.startQuat = control.quaternion.clone();
    }

    const t = Math.min(
      1,
      (performance.now() - this.originState.startTime) /
      this.originState.duration
    );
    const ease = t * t * (3 - 2 * t); // smoothstep

    // lerp position
    control.position.lerpVectors(
      this.originState.startPos,
      new Vector3(0, 0, 0),
      ease
    );

    // slerp rotation
    control.quaternion.slerpQuaternions(
      this.originState.startQuat,
      new Quaternion(),
      ease
    );

    if (t === 1) {
      this.originState.active = false;
    }
  }

  private handleSlideGesture(
    motion: Vector2[],
    deltaTime: number,
    control: Object3D
  ) {
    if (motion[0] && motion[1]) {
      const totalMotion = motion[0]?.clone().add(motion[1]);
      const slide = new Vector3(
        -totalMotion.x * CC.TWO_FINGER_SLIDE_SPEED,
        totalMotion.y * CC.TWO_FINGER_SLIDE_SPEED,
        0
      );
      slide.multiplyScalar(this.panFriction);
      slide.applyQuaternion(control.quaternion);
      control.position.add(slide);
      this.moveVelocity = slide.clone().multiplyScalar(1 / deltaTime);
    }
  }

  private handlePinchGesture(
    motion: Vector2[],
    deltaTime: number,
    control: Object3D
  ) {
    if (!(this.sliding?.last && this.panning?.last)) {
      return;
    }

    const deltaDir = this.sliding.last.clone().sub(this.panning.last);
    const normalizedDelta = deltaDir.normalize();
    const motionDir = [
      motion[0]?.dot(normalizedDelta),
      motion[1]?.dot(normalizedDelta),
    ];

    if (motionDir[0] && motionDir[1]) {
      const pinchOut = motionDir[1] - motionDir[0];
      const slide = new Vector3(0, 0, -pinchOut * CC.PINCH_SPEED);
      slide.applyQuaternion(control.quaternion);
      control.position.add(slide);
      this.moveVelocity = slide.clone().multiplyScalar(1 / deltaTime);
    }
  }

  // dragging with the finger accross the screen to rotate the camera
  private updateSinglePointerRotation(deltaTime: number, control: Object3D) {
    const pan = new Vector3();

    if (this.panning && !this.dualPress) {
      const delta = this.panning.position.clone().sub(this.panning.last);
      this.panning.last.copy(this.panning.position);

      pan.set(-delta.x, -delta.y, 0);
      pan.multiplyScalar(this.rotateSpeed);
      pan.x *= this.invertX ? -1 : 1;
      pan.y *= this.invertY ? -1 : 1;
      this.rotateVelocity = pan.clone().multiplyScalar(1 / deltaTime);
      if (
        this.rotateVelocity.lengthSq() <
        this.SMALL_ROTATE_EPS * this.SMALL_ROTATE_EPS
      ) {
        this.rotateVelocity.set(0, 0, 0);
      }
    } else {
      this.rotateVelocity.multiplyScalar(
        Math.exp(-deltaTime / this.panInertia)
      );
      if (
        this.rotateVelocity.lengthSq() <
        this.SMALL_ROTATE_EPS * this.SMALL_ROTATE_EPS
      ) {
        this.rotateVelocity.set(0, 0, 0);
      }
      pan.addScaledVector(this.rotateVelocity, deltaTime);
    }

    // apply virtual joystick look input with gentle smoothing
    this.lookInputSmooth.lerp(this.lookInput, this.lookEasing);
    if (this.lookInputSmooth.lengthSq() > 1) {
      this.lookInputSmooth.normalize();
    }
    // snap to exact zero near center to avoid infinitesimal drift
    if (
      this.lookInput.lengthSq() === 0 &&
      this.lookInputSmooth.lengthSq() < this.SMALL_LOOK_EPS
    ) {
      this.lookInputSmooth.set(0, 0);
    } else if (this.lookInputSmooth.lengthSq() > this.SMALL_LOOK_EPS) {
      const lookDelta = new Vector3(
        this.lookInputSmooth.x * this.lookSpeed * deltaTime,
        this.lookInputSmooth.y * this.lookSpeed * deltaTime,
        0
      );
      // respect invert settings for joystick-look as well
      lookDelta.x *= this.invertX ? -1 : 1;
      lookDelta.y *= this.invertY ? -1 : 1;
      pan.add(lookDelta);
      const lookVelocity = lookDelta.clone().multiplyScalar(
        1 / Math.max(deltaTime, 1e-5)
      );
      this.rotateVelocity.lerp(lookVelocity, 0.2);
      if (
        this.rotateVelocity.lengthSq() <
        this.SMALL_ROTATE_EPS * this.SMALL_ROTATE_EPS
      ) {
        this.rotateVelocity.set(0, 0, 0);
      }
    }

    if (pan.lengthSq() < this.SMALL_PAN_EPS * this.SMALL_PAN_EPS) {
      pan.set(0, 0, 0);
    }
    const eulers = new Euler().setFromQuaternion(control.quaternion, "YXZ");
    eulers.y -= pan.x;
    eulers.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, eulers.x - pan.y));
    control.quaternion.setFromEuler(eulers);

    this.moveVelocity.multiplyScalar(Math.exp(-deltaTime / this.panInertia));
    if (this.moveVelocity.lengthSq() < this.SMALL_PAN_EPS * this.SMALL_PAN_EPS) {
      this.moveVelocity.set(0, 0, 0);
    } else {
      control.position.addScaledVector(this.moveVelocity, deltaTime);
    }
  }

  private updateScrollInput(control: Object3D) {
    const scroll = this.scroll.multiplyScalar(this.scrollSpeed);
    scroll.set(scroll.x, scroll.z, scroll.y);
    if (this.reverseScroll) {
      scroll.multiplyScalar(-1);
    }

    if (this.scrollVerticalEnabled) {
      // normal scroll -- full camera rotation (includes pitch)
      scroll.applyQuaternion(control.quaternion);
    } else {
      // restricted scroll -- only camera yaw (no pitch)
      const euler = new Euler().setFromQuaternion(control.quaternion, "YXZ");
      const yawOnlyQuaternion = new Quaternion().setFromEuler(
        new Euler(0, euler.y, 0, "YXZ")
      );
      scroll.applyQuaternion(yawOnlyQuaternion);
    }

    if (scroll.lengthSq() > this.SMALL_SCROLL_EPS * this.SMALL_SCROLL_EPS) {
      control.position.add(scroll);
    }
    this.scroll.set(0, 0, 0);
  }

  private updateDualPointerGestures(deltaTime: number, control: Object3D) {
    if (!(this.dualPress && this.panning && this.sliding)) {
      return;
    }

    const motion = [
      this.panning.position.clone().sub(this.panning.last),
      this.sliding.position.clone().sub(this.sliding.last),
    ];

    if (motion[0] && motion[1]) {
      const coincidence = motion[0].dot(motion[1]);

      if (coincidence >= 0.2) {
        this.handleSlideGesture(motion, deltaTime, control);
      } else if (coincidence <= -0.2) {
        this.handlePinchGesture(motion, deltaTime, control);
      }
    }

    this.panning.last.copy(this.panning.position);
    this.sliding.last.copy(this.sliding.position);
  }

  setInvertSettings(invertX: boolean, invertY: boolean) {
    if (this.isMobile) {
      this.invertX = false;
      this.invertY = false;
    } else {
      this.invertX = invertX;
      this.invertY = invertY;
    }
  }

  update(deltaTime: number, control: Object3D) {
    this.updateReturnToOrigin(control);
    this.updateBurstMovement();

    if (this.dualPress && this.panning && this.sliding) {
      this.updateDualPointerGestures(deltaTime, control);
    } else {
      this.updateSinglePointerRotation(deltaTime, control);
    }

    this.updateScrollInput(control);

    if (this.camera && "fov" in this.camera) {
      const camera = this.camera as {
        fov: number;
        updateProjectionMatrix?: () => void;
      };
      if (this.fovAnimating) {
        // linearly interpolate FOV toward target using deltaTime-based speed
        const delta = CC.FOV_LERP_SPEED * deltaTime; // degrees per second
        const diff = this.fovTarget - camera.fov;
        const step = Math.sign(diff) * Math.min(Math.abs(diff), delta);
        const next = camera.fov + step;
        camera.fov = next;
        camera.updateProjectionMatrix?.();
        if (Math.abs(this.fovTarget - next) <= 1e-3) {
          camera.fov = this.fovTarget;
          camera.updateProjectionMatrix?.();
          this.fovAnimating = false;
        }
      } else {
        this.fovStart = camera.fov;
        this.fovTarget = this.clampFovDeg(this.fovTarget || camera.fov);
      }
    }
  }
}
